import { Box, Heading, Stack, VStack } from "@biblioteksentralen/react";
import { sort } from "radash";
import { useRef } from "react";
import { DefaultContainer } from "../../../components/ContentContainer";
import AppealTerms from "../../../components/forrigeBok/AppealTerms";
import ReadAlikes from "../../../components/forrigeBok/ReadAlikes";
import { useCommonData } from "../../../components/layout/CommonDataProvider";
import { useTranslation } from "../../../utils/hooks/useTranslation";
import { getDocumentTypeDefiniteNoun } from "../../cordata/documentTypes";
import { getSearchConfig } from "../../searchApi/searchConfig";
import { SubjectLinks } from "../SubjectLinks";
import { CollapsableWithGradient } from "./CollapsableWithGradient";
import { KulturfondVideoView } from "./KulturfondVideoView";
import RecommendationCardForWorkView from "./RecommendationCardForWorkView";
import { WorkViewBanner } from "./WorkViewBanner/WorkViewBanner";
import { getSortedLanguagesListIndex } from "@libry-content/integrations";
import { useWorkViewContext } from "./WorkViewContextProvider";
import ManifestationCard from "../manifestations/ManifestationCard";

export const WorkView = () => {
  const { t, ts } = useTranslation();
  const manifestationsTitleRef = useRef<null | HTMLAnchorElement>(null);
  const { site } = useCommonData();

  const searchConfig = getSearchConfig(site);
  if (!searchConfig.isSearchIntegrationEnabled) {
    console.error("Search integration not enabled for this site");
  }

  const { work, holdingsData, forrigeBokWork, currentVariant, recommendation, kulturFondVideo, hasValidHoldings } =
    useWorkViewContext();

  if (!work) return null;
  const workTriples = work.getWorkTriples();

  return (
    <Box margin={{ base: "1rem 0 6rem", md: "2rem 0 8rem" }}>
      {/* We keep the banner outside of the container, since part of it is supposed to cover entire width. */}
      <WorkViewBanner manifestationsTitleRef={manifestationsTitleRef} />
      <DefaultContainer marginTop="2rem">
        <Stack padding={{ sm: "1rem", lg: 0 }} spacing="3rem">
          {recommendation && (
            <Box>
              <Heading as="h2" fontSize="xl">
                {recommendation.isLocal ? t("Anbefaling fra biblioteket") : t("Anbefaling fra et annet bibliotek")}
              </Heading>
              <RecommendationCardForWorkView recommendation={recommendation} />
            </Box>
          )}

          {(work.summary || work.subjects.length > 0) && (
            <Box>
              <Heading as="h2" fontSize="xl" marginBottom="1rem">
                {t("Om")} {ts(getDocumentTypeDefiniteNoun(currentVariant?.variant.documentType))}
              </Heading>
              {work.subjects.length > 0 && <SubjectLinks subjects={work.subjects} marginBottom="1rem" />}
              {work.summary && (
                <CollapsableWithGradient
                  maxWidth="30rem"
                  readLessText={t("Les mindre")}
                  readMoreText={t("Les hele beskrivelsen")}
                  isOverflowingGuess={work.summary.length > 150}
                >
                  {work.summary}
                </CollapsableWithGradient>
              )}
            </Box>
          )}
          <KulturfondVideoView kulturFondVideo={kulturFondVideo} />
          {forrigeBokWork && (
            <Box>
              <Heading as="h2" fontSize="xl">
                {t("Leseopplevelse")}
              </Heading>
              <Box marginTop="1rem">
                <AppealTerms appealTerms={forrigeBokWork.appealTerms} />
              </Box>
            </Box>
          )}
          <ReadAlikes workId={work.id} />
        </Stack>

        {hasValidHoldings && (
          <>
            <Heading as="h2" fontSize="xl" margin="2rem 0 1rem 0 !important">
              {t("Utgaver")}
            </Heading>

            {holdingsData?.hasError && t("Noe gikk galt ved henting av beholdningsstatus.")}

            {!holdingsData?.hasError && (
              <VStack spacing="1rem" margin="0 1rem">
                {sort(workTriples, ({ languages }) => getSortedLanguagesListIndex(languages)).map((workTriple, idx) => (
                  <ManifestationCard
                    key={workTriple.manifestationId}
                    workTriple={workTriple}
                    ref={idx === 0 ? manifestationsTitleRef : undefined}
                  />
                ))}
              </VStack>
            )}
          </>
        )}
      </DefaultContainer>
    </Box>
  );
};
